import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Box,
  IconButton,
  Collapse,
  Button
} from '@mui/material';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import ManualMethodEntry from './ManualMethodEntry';

const MethodsList = ({ methods = [], serviceType, onMethodsChange }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const handleAddMethod = (newMethod) => {
    onMethodsChange([...methods, newMethod]);
  };

  const handleDeleteMethod = (index) => {
    const newMethods = methods.filter((_, i) => i !== index);
    onMethodsChange(newMethods);
  };

  const toggleRow = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const MethodDetails = ({ method }) => {
    console.log('Method object:', method);
    // Parameters'ı JSON.parse ile parse et
    const parsedParameters = React.useMemo(() => {
      try {
        return typeof method.parameters === 'string' ? JSON.parse(method.parameters) : method.parameters || [];
      } catch (e) {
        console.error('Failed to parse parameters:', e);
        return [];
      }
    }, [method.parameters]);

    // Responses'ı JSON.parse ile parse et
    const parsedResponses = React.useMemo(() => {
      try {
        return typeof method.responses === 'string' ? JSON.parse(method.responses) : method.responses || [];
      } catch (e) {
        console.error('Failed to parse responses:', e);
        return [];
      }
    }, [method.responses]);
  
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Description: {method.description || 'No description provided'}
        </Typography>
  
        {/* Parameters Section */}
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Parameters</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Required</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedParameters.map((param, index) => (
                <TableRow key={index}>
                  <TableCell>{param.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${param.type}${param.isArray ? '[]' : ''}`}
                      size="small" 
                      variant="outlined"
                      color={param.objectRef ? 'info' : 'default'}
                    />
                    {param.objectRef && (
                      <Chip 
                        label={`Ref: ${param.objectRef}`}
                        size="small" 
                        variant="outlined"
                        color="primary"
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{param.location}</TableCell>
                  <TableCell>
                    <Chip 
                      label={param.required ? 'Required' : 'Optional'}
                      size="small"
                      color={param.required ? 'error' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{param.description}</TableCell>
                </TableRow>
              ))}
              {parsedParameters.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No parameters
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
  
        {/* Responses Section */}
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Responses</Typography>
        {parsedResponses.map((response, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  label={`Status: ${response.statusCode}`}
                  color={response.statusCode.startsWith('2') ? 'success' : 
                         response.statusCode.startsWith('4') ? 'warning' : 
                         response.statusCode.startsWith('5') ? 'error' : 'default'}
                />
                <Typography variant="body2">{response.description}</Typography>
                {response.type && (
                  <Chip
                    label={`Type: ${response.type}${response.isArray ? '[]' : ''}`}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                )}
                {response.objectRef && (
                  <Chip
                    label={`Ref: ${response.objectRef}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
              </Box>
              
              {response.parameters && response.parameters.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Required</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {response.parameters.map((param, pIndex) => (
                        <TableRow key={pIndex}>
                          <TableCell>{param.name}</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${param.type}${param.isArray ? '[]' : ''}`}
                              size="small"
                              variant="outlined"
                              color={param.objectRef ? 'info' : 'default'}
                            />
                            {param.objectRef && (
                              <Chip 
                                label={`Ref: ${param.objectRef}`}
                                size="small" 
                                variant="outlined"
                                color="primary"
                                sx={{ ml: 0.5 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={param.required ? 'Required' : 'Optional'}
                              size="small"
                              color={param.required ? 'error' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{param.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        ))}
        {parsedResponses.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No responses defined
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <div>
      <Box sx={{ mb: 2 }}>
        <ManualMethodEntry onAddMethod={handleAddMethod} />
      </Box>
      
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Name</TableCell>
              <TableCell>{serviceType === 'rest' ? 'HTTP Method' : 'SOAP Action'}</TableCell>
              <TableCell>{serviceType === 'rest' ? 'Path' : 'Namespace'}</TableCell>
              <TableCell>Parameters</TableCell>
              <TableCell>Responses</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {methods.map((method, index) => (
              <React.Fragment key={index}>
                <TableRow 
                  hover
                  onClick={() => toggleRow(index)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <TableCell padding="checkbox">
                    <IconButton size="small">
                      {expandedRow === index ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{method.name}</TableCell>
                  <TableCell>
                    {serviceType === 'rest' ? (
                      <Chip 
                        label={method.httpMethod} 
                        size="small"
                        color={
                          method.httpMethod === 'GET' ? 'primary' :
                          method.httpMethod === 'POST' ? 'success' :
                          method.httpMethod === 'PUT' ? 'warning' :
                          method.httpMethod === 'DELETE' ? 'error' : 'default'
                        }
                      />
                    ) : (
                      method.soapAction
                    )}
                  </TableCell>
                  <TableCell>{serviceType === 'rest' ? method.path : method.namespace}</TableCell>
                  <TableCell>
                    {method.parameters?.length || 0} parameter(s)
                  </TableCell>
                  <TableCell>
                    {method.responses?.length || 0} response(s)
                    {method.responses?.map((response, idx) => {
                      const successResponse = response.statusCode === "200";
                      const hasType = response.objectRef || response.type;
                      
                      if (successResponse && hasType) {
                        let displayType = response.objectRef || response.type;
                        if (response.isArray) {
                          displayType = `${displayType}[]`;
                        }
                        
                        return (
                          <Chip
                            key={idx}
                            label={`Returns: ${displayType}`}
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{ ml: 1 }}
                          />
                        );
                      }
                      return null;
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMethod(index);
                      }}
                      color="error"
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0, borderBottom: 'none' }}>
                    <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                      <MethodDetails method={method} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {methods.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No methods defined yet. Use the "Add Method" button to add methods manually, 
                    or use the service discovery feature.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default MethodsList;